// Phase 1: Planning & Scoping Data

export const phase1Data = {
  phaseNumber: 1,
  phaseName: "Planning & Scoping",
  duration: "1-2 weeks",
  objective: "Establish clear review objectives, define scope boundaries, identify stakeholders, and create a structured plan for conducting the governance review.",

  sections: [
    {
      id: "review-objectives",
      title: "Review Objectives & Success Criteria",
      description: "Define what you aim to achieve through this governance review. Objectives must be specific, measurable, and aligned to organisational context.",
      
      guidanceQuestions: [
        "What prompted this governance review? (e.g., regulatory requirement, funder expectation, board concern, strategic change)",
        "What specific governance concerns or questions need to be addressed?",
        "What decisions will be informed by this review?",
        "Who will use the findings and for what purpose?",
        "What would 'success' look like for this review?"
      ],
      
      commonObjectives: [
        "Assess overall governance effectiveness and identify improvement areas",
        "Ensure compliance with Charity Commission and Companies House requirements",
        "Evaluate board composition and capability gaps",
        "Assess quality of financial oversight and controls",
        "Review risk governance effectiveness",
        "Prepare for strategic planning cycle or major change",
        "Respond to funder governance requirements",
        "Support CEO transition or board renewal"
      ],
      
      outputRequired: "Review Objectives Statement (3-5 specific objectives)"
    },

    {
      id: "scope-definition",
      title: "Scope Definition",
      description: "Clearly define what is included and excluded from the review. Scope must be realistic given available time and resources.",
      
      scopeElements: {
        governance: {
          title: "Governance Areas",
          inScope: [
            "Board structure and composition",
            "Board effectiveness and performance",
            "Committee structure and effectiveness",
            "Scheme of delegation and decision-making",
            "Strategic oversight quality",
            "Financial oversight and controls",
            "Risk governance",
            "Regulatory compliance",
            "Information governance",
            "Stakeholder accountability"
          ],
          considerOutOfScope: [
            "Detailed operational processes (unless governance-related)",
            "Individual trustee performance reviews",
            "HR policies (unless governance-related)",
            "Detailed financial audit (beyond governance oversight)",
            "Fundraising effectiveness (beyond board oversight)"
          ]
        },
        
        temporal: {
          title: "Time Period",
          recommended: "Review documentation and board performance over the last 12-18 months",
          rationale: "Provides sufficient evidence while remaining current and actionable"
        },
        
        stakeholders: {
          title: "Stakeholders to Involve",
          core: [
            "Chair",
            "CEO / Executive Director",
            "Trustees (all or representative sample)",
            "Committee Chairs"
          ],
          optional: [
            "Finance Director / CFO",
            "Company Secretary (if applicable)",
            "Selected senior management"
          ]
        }
      },
      
      outputRequired: "Scope Statement (1-2 pages) clearly stating what is in/out of scope"
    },

    {
      id: "stakeholder-mapping",
      title: "Stakeholder Identification & Mapping",
      description: "Identify all governance actors and map their roles, responsibilities, and contribution to the review.",
      
      stakeholderCategories: [
        {
          role: "Chair",
          reviewRole: "Primary interview subject; owns board effectiveness",
          requiredInput: ["Board performance assessment", "Board dynamics", "Relationship with CEO", "Strategic oversight"]
        },
        {
          role: "CEO / Executive Director",
          reviewRole: "Primary interview subject; perspective on board effectiveness and information flow",
          requiredInput: ["Executive perspective on governance", "Board support quality", "Information adequacy", "Decision-making clarity"]
        },
        {
          role: "Trustees",
          reviewRole: "Interview subjects (sample or all depending on board size)",
          requiredInput: ["Individual trustee perspectives", "Role clarity", "Engagement", "Capability assessment"]
        },
        {
          role: "Committee Chairs",
          reviewRole: "Interview subjects for committee-specific assessments",
          requiredInput: ["Committee effectiveness", "Terms of reference adequacy", "Information flow", "Decision-making"]
        },
        {
          role: "Finance Lead",
          reviewRole: "Interview subject (optional) for financial governance deep-dive",
          requiredInput: ["Financial controls", "Reporting quality", "Board financial literacy"]
        },
        {
          role: "Company Secretary / Governance Lead",
          reviewRole: "Document provider and process expert",
          requiredInput: ["Governance documentation", "Compliance status", "Process insights"]
        }
      ],
      
      outputRequired: "Stakeholder Map with roles and interview schedule"
    },

    {
      id: "document-requirements",
      title: "Documentation Requirements",
      description: "Identify all governance documents required for the review. Comprehensive document gathering is essential for Phase 2.",
      
      documentCategories: [
        {
          category: "Constitutional & Structural",
          documents: [
            "Articles of Association / Constitution / Trust Deed (current version)",
            "Charity Commission registration details",
            "Companies House records (if applicable)",
            "Board terms of reference",
            "Committee terms of reference (all committees)",
            "Scheme of delegation",
            "Reserved matters list",
            "Organisational structure chart"
          ],
          criticality: "Essential"
        },
        {
          category: "Board Performance & Oversight",
          documents: [
            "Board minutes (last 12-18 months)",
            "Committee minutes (last 12-18 months)",
            "Board papers / information packs (sample from last 6 months)",
            "Board attendance records",
            "Board skills matrix / competency framework (if exists)",
            "Board evaluation reports (if conducted)",
            "Trustee induction materials"
          ],
          criticality: "Essential"
        },
        {
          category: "Strategic & Financial Governance",
          documents: [
            "Current strategic plan",
            "Annual business / operational plan",
            "Financial regulations / financial controls policy",
            "Reserves policy",
            "Investment policy (if applicable)",
            "Annual budget",
            "Management accounts (last 6-12 months)",
            "Annual report and accounts (last 2 years)",
            "Audit management letters (last 2 years)"
          ],
          criticality: "Essential"
        },
        {
          category: "Risk & Compliance",
          documents: [
            "Risk register (current version)",
            "Risk management policy / framework",
            "Internal controls documentation",
            "Conflicts of interest policy",
            "Register of interests",
            "Whistleblowing policy",
            "Fraud policy",
            "Insurance schedule"
          ],
          criticality: "Essential"
        },
        {
          category: "Policies & Procedures",
          documents: [
            "Governance calendar / forward planner",
            "Policy register / schedule",
            "Safeguarding policy",
            "Data protection policy",
            "Equality & diversity policy",
            "Health & safety policy",
            "Environmental policy (if applicable)",
            "Communications / media policy"
          ],
          criticality: "Important"
        },
        {
          category: "Regulatory & External",
          documents: [
            "Charity Commission annual returns (last 2 years)",
            "Serious incident reports (if any)",
            "Regulatory correspondence",
            "Funder requirements / grant agreements (governance-related)",
            "Membership agreements (if applicable)"
          ],
          criticality: "Important"
        }
      ],
      
      documentRequestProcess: {
        step1: "Prepare formal document request letter/email",
        step2: "Specify document formats (originals, current versions)",
        step3: "Set realistic deadline (typically 1-2 weeks)",
        step4: "Designate single point of contact for document provision",
        step5: "Create secure document repository for review",
        step6: "Log all documents received and track gaps"
      },
      
      outputRequired: "Document Request List and tracking log"
    },

    {
      id: "interview-planning",
      title: "Interview Schedule & Planning",
      description: "Plan structured interviews with key governance stakeholders. Interviews are critical for Phase 3 effectiveness assessment.",
      
      interviewApproach: {
        format: "1-to-1 structured interviews",
        duration: "45-90 minutes depending on role",
        method: "In-person or video call (not email questionnaire)",
        structure: "Semi-structured with core questions and follow-up probes",
        confidentiality: "Anonymized synthesis (no individual attribution unless critical)"
      },
      
      interviewSchedule: [
        {
          stakeholder: "Chair",
          priority: "Essential",
          timing: "Early in Phase 3",
          duration: "90 minutes",
          focus: ["Board effectiveness", "Board dynamics", "Strategic oversight", "CEO relationship", "Future challenges"]
        },
        {
          stakeholder: "CEO",
          priority: "Essential",
          timing: "Early in Phase 3",
          duration: "90 minutes",
          focus: ["Board support quality", "Information adequacy", "Decision-making", "Role clarity", "Governance challenges"]
        },
        {
          stakeholder: "Trustees",
          priority: "Essential",
          timing: "Mid Phase 3",
          duration: "45-60 minutes each",
          approach: "Sample of 4-6 if large board, all if ≤8 trustees",
          focus: ["Individual perspectives", "Engagement", "Role clarity", "Information quality", "Capability"]
        },
        {
          stakeholder: "Committee Chairs",
          priority: "Important",
          timing: "Mid Phase 3",
          duration: "45-60 minutes each",
          focus: ["Committee effectiveness", "Terms of reference", "Reporting to board", "Resource adequacy"]
        },
        {
          stakeholder: "Finance Lead",
          priority: "Optional",
          timing: "Late Phase 3",
          duration: "45 minutes",
          focus: ["Financial governance", "Controls", "Reporting", "Board financial literacy"]
        }
      ],
      
      preparationRequirements: [
        "Book interview slots in advance (minimum 2 weeks notice)",
        "Send interview guide/questions in advance",
        "Explain confidentiality and synthesis approach",
        "Confirm interview location/platform",
        "Prepare note-taking method (notes, not recording unless agreed)",
        "Allow buffer time between interviews for synthesis"
      ],
      
      outputRequired: "Interview Schedule with confirmed dates and logistics"
    },

    {
      id: "evaluation-criteria",
      title: "Evaluation Criteria & Scoring Framework",
      description: "Establish the criteria against which governance will be assessed. This ensures consistency and objectivity.",
      
      assessmentDimensions: [
        "Governance Structure & Documentation",
        "Board Composition & Capability",
        "Strategic Oversight Quality",
        "Financial Governance & Controls",
        "Risk Governance Effectiveness",
        "Regulatory Compliance",
        "Decision-Making Processes",
        "Information Governance",
        "Governance Culture & Behaviors",
        "Stakeholder Accountability"
      ],
      
      ragRatingCriteria: {
        red: "Significant governance weakness, regulatory risk, or control failure requiring immediate action",
        amber: "Governance gaps, inconsistencies, or areas not meeting best practice requiring improvement within defined timeframe",
        green: "Governance standards met, controls effective, best practice evident with only minor enhancements needed"
      },
      
      evidenceStandards: {
        documentary: "Written evidence from policies, minutes, reports, registers",
        interview: "Stakeholder testimony from structured interviews",
        observational: "Quality indicators from documentation review and process observation",
        triangulation: "Multiple evidence sources must support findings and ratings"
      },
      
      outputRequired: "Agreed evaluation criteria and scoring approach"
    },

    {
      id: "timeline-milestones",
      title: "Review Timeline & Milestones",
      description: "Create a realistic project timeline with clear milestones and dependencies.",
      
      typicalTimeline: {
        phase1: "1-2 weeks (Planning & Scoping)",
        phase2: "2-3 weeks (Documentation Review)",
        phase3: "2-3 weeks (Effectiveness Assessment)",
        phase4: "1-2 weeks (Reporting & Planning)",
        total: "6-10 weeks end-to-end"
      },
      
      criticalMilestones: [
        { milestone: "Review objectives agreed", week: 1 },
        { milestone: "Scope finalized", week: 1 },
        { milestone: "Documents requested", week: 1 },
        { milestone: "Interviews scheduled", week: 2 },
        { milestone: "All documents received", week: 3 },
        { milestone: "Document review complete", week: 5 },
        { milestone: "All interviews complete", week: 7 },
        { milestone: "Findings synthesized", week: 8 },
        { milestone: "Draft report complete", week: 9 },
        { milestone: "Final report delivered", week: 10 }
      ],
      
      dependencies: [
        "Document review cannot start until documents received",
        "Interviews should follow initial document review",
        "Report writing requires both documentation and interview phases complete",
        "Board presentation timing depends on board meeting schedule"
      ],
      
      outputRequired: "Project timeline with milestones and responsibilities"
    }
  ],

  completionCriteria: {
    readyForPhase2: [
      "Review objectives documented and agreed",
      "Scope statement finalized",
      "Stakeholders identified and mapped",
      "Document request issued and acknowledgement received",
      "Interview schedule agreed and booked",
      "Evaluation criteria established",
      "Project timeline created",
      "Evidence tracking system established"
    ]
  },

  commonPitfalls: [
    {
      pitfall: "Undefined or overly broad scope",
      consequence: "Review becomes unmanageable or never completes",
      mitigation: "Be specific about boundaries. It's better to do a focused review well than a comprehensive one poorly."
    },
    {
      pitfall: "Inadequate document collection",
      consequence: "Phase 2 delayed or incomplete",
      mitigation: "Issue comprehensive document request early. Follow up proactively. Accept what's available and document gaps."
    },
    {
      pitfall: "Interview scheduling delays",
      consequence: "Phase 3 timeline extends significantly",
      mitigation: "Book interviews early. Build buffer time. Confirm 48 hours before each interview."
    },
    {
      pitfall: "Unrealistic timeline",
      consequence: "Review quality compromised or deadlines missed",
      mitigation: "Allow realistic time for each phase. Build contingency. Better to set conservative timeline and deliver early."
    }
  ]
};
